import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { CURRENCY_PRECISION, roundTo } from '@vy/number';
import { type CustomerRevenueReportItem, REVENUE_RECOGNITION_TYPE } from '@vy/types';
import { isNotNullOrUndefined } from '@vy/utils';
import { useEffect, useMemo, useState } from 'react';
import { VyDatePicker } from '../new-design/date-picker';
import { useUpsertRevenueRecognitionOverride } from '../../hooks/requests/use-revenue-recognition-override';
import { applyPointInTime } from './apply-point-in-time';
import { applyStraightLine } from './apply-straight-line';
import { formatAmount } from './format-amount';
import { getDefaultRevenueRecognitionType } from './get-default-revenue-recognition-type';
import { getPricingModelLabel } from './get-pricing-model-label';
import { PointInTimeEditor } from './PointInTimeEditor';
import {
  computeRecognitionTransition,
  getContractStartDay,
  normalizeSinglePoint,
} from './recognition-type-transition';
import {
  REVENUE_RECOGNITION_OPTIONS,
  SUM_TOLERANCE,
  type RevenuePoint,
  type RevenueRecognitionType,
} from './types';

type Props = {
  row: CustomerRevenueReportItem | null;
  contractId?: string;
  onClose: () => void;
  onSuccess?: () => void;
};

function getVariantName(row: CustomerRevenueReportItem): string {
  return (
    row.variant?.name
    ?? row.product?.name
    ?? row.productVariant?.name
    ?? row.revisionedLineItem?.name
    ?? 'Unknown'
  );
}

function getPricingModelString(row: CustomerRevenueReportItem): string | null | undefined {
  return row.variant?.pricingModel ?? row.productVariant?.pricing?.pricingModel ?? null;
}

function seedPointsFromRow(row: CustomerRevenueReportItem): RevenuePoint[] {
  if (row.revenue && row.revenue.length > 0) {
    return row.revenue
      .map((record) => ({ date: new Date(record.date), amount: roundTo(record.value, CURRENCY_PRECISION) }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  const fallbackStart = row.contract?.startDate ? new Date(row.contract.startDate) : new Date();
  const fallbackTotal = roundTo(row.totalRevenue ?? row.totalBilled ?? 0, CURRENCY_PRECISION);
  return [{ date: fallbackStart, amount: fallbackTotal }];
}

function toFirstOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function toLastOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function computeSessionTotal(row: CustomerRevenueReportItem): number {
  let total = 0;
  for (const year of Object.keys(row.yearlyRevenue)) {
    const months = row.yearlyRevenue[year];
    for (const month of Object.keys(months)) {
      total += months[month as keyof typeof months]?.revenue ?? 0;
    }
  }
  if (total > 0) return total;
  return row.totalRevenue ?? row.totalBilled ?? 0;
}

function countRevenueMonths(row: CustomerRevenueReportItem): number {
  let count = 0;

  for (const year of Object.keys(row.yearlyRevenue)) {
    const months = row.yearlyRevenue[year];

    for (const month of Object.keys(months)) {
      if ((months[month as keyof typeof months]?.revenue ?? 0) !== 0) {
        count++;
      }
    }
  }

  return count;
}

export const ProductInspectDialog: React.FC<Props> = ({ row, contractId, onClose, onSuccess }) => {
  const variantId = row?.variantId ?? row?.variant?.id ?? '';
  const mutation = useUpsertRevenueRecognitionOverride(variantId, contractId, {
    onSuccess: () => {
      onSuccess?.();
      onClose();
    },
  });

  const [recognitionType, setRecognitionType] = useState<RevenueRecognitionType>(
    REVENUE_RECOGNITION_TYPE.StraightLine,
  );
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [points, setPoints] = useState<RevenuePoint[]>([]);
  // Captured on dialog open and held fixed for this edit session — equivalent
  // to the demo's `_originalTotalRevenue` cache. Keeps straight-line
  // re-applies idempotent w.r.t. date-range tweaks.
  const [sessionTotal, setSessionTotal] = useState(0);

  useEffect(() => {
    if (!row) return;
    setRecognitionType(
      row.recognitionTypeOverride
        ?? getDefaultRevenueRecognitionType(getPricingModelString(row), countRevenueMonths(row)),
    );
    setStartDate(row.contract?.startDate ? toFirstOfMonth(new Date(row.contract.startDate)) : null);
    setEndDate(row.contract?.endDate ? toLastOfMonth(new Date(row.contract.endDate)) : null);
    setPoints(seedPointsFromRow(row));
    setSessionTotal(computeSessionTotal(row));
  }, [row]);

  const productName = useMemo(() => (row ? getVariantName(row) : ''), [row]);
  const pricingModelLabel = useMemo(
    () => (row ? getPricingModelLabel(getPricingModelString(row)) : '--'),
    [row],
  );

  const hasContract = isNotNullOrUndefined(row?.contract);
  const isUsageBased = recognitionType === REVENUE_RECOGNITION_TYPE.UsageBased;
  const isPointInTime = recognitionType === REVENUE_RECOGNITION_TYPE.PointInTime;
  const isMilestones = recognitionType === REVENUE_RECOGNITION_TYPE.Milestones;
  // Both Point-in-Time and Milestones edit per-date points and submit via applyPointInTime.
  const isPointBased = isPointInTime || isMilestones;

  // Point-in-Time is a single full-value record, so the amount is locked to the
  // session total — keep points[0] in sync and collapse any multi-record data.
  useEffect(() => {
    if (!row || !isPointInTime || points.length === 0) return;
    if (points.length === 1 && Math.abs(points[0].amount - sessionTotal) <= SUM_TOLERANCE) return;
    setPoints(normalizeSinglePoint(points, sessionTotal, getContractStartDay(row)));
    // Deps intentionally exclude `points` to avoid a re-run loop; runs on type/total change.
  }, [row, isPointInTime, sessionTotal]);

  const pointsSum = useMemo(() => points.reduce((acc, p) => acc + p.amount, 0), [points]);
  const pointsSumValid = Math.abs(pointsSum - sessionTotal) <= SUM_TOLERANCE;

  const inFlight = mutation.isLoading;
  const applyDisabled =
    !row
    || !hasContract
    || isUsageBased
    || inFlight
    || (isPointBased && !pointsSumValid)
    || (!isPointBased && (!startDate || !endDate));

  const handleRecognitionTypeChange = (next: RevenueRecognitionType) => {
    setRecognitionType(next);
    if (!row) return;
    const { points: nextPoints } = computeRecognitionTransition({
      nextType: next,
      row,
      sessionTotal,
      currentPoints: points,
    });
    if (nextPoints) setPoints(nextPoints);
  };

  const handleApply = () => {
    if (!row || !hasContract || isUsageBased) return;

    const revenueOverride = isPointBased
      ? applyPointInTime(points)
      : startDate && endDate
        ? applyStraightLine(startDate, endDate, sessionTotal)
        : [];

    if (revenueOverride.length === 0) return;

    mutation.mutate({ revenueOverride, recognitionTypeOverride: recognitionType });
  };

  return (
    <Dialog
      open={row !== null}
      onClose={inFlight ? undefined : onClose}
      slotProps={{ paper: { sx: { width: 760, maxWidth: 760 } } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', p: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 500 }}>
            Edit revenue recognition type
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Select the revenue recognition type for <b>&apos;{productName}&apos;</b>
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          disabled={inFlight}
          data-vy="product-inspect-dialog-close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2, pb: 1 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label="Pricing type"
            value={pricingModelLabel}
            disabled
            sx={{ flex: 1 }}
            data-vy="product-inspect-dialog-pricing-type"
          />
          <TextField
            label="Revenue recognition type"
            select
            value={recognitionType}
            onChange={(event) => handleRecognitionTypeChange(event.target.value as RevenueRecognitionType)}
            disabled={!hasContract || isUsageBased || inFlight}
            sx={{ flex: 1 }}
            data-vy="product-inspect-dialog-recognition-type"
            slotProps={{
              select: {
                renderValue: (value) => {
                  const option = REVENUE_RECOGNITION_OPTIONS.find((o) => o.key === value);
                  return option?.label ?? String(value);
                },
              },
            }}
          >
            {REVENUE_RECOGNITION_OPTIONS.map((option) => (
              <MenuItem key={option.key} value={option.key} disabled={option.disabled}>
                <Box>
                  <Typography variant="body2">{option.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Amount"
            value={formatAmount(row?.totalRevenue ?? row?.totalBilled ?? sessionTotal)}
            disabled
            sx={{ flex: 1 }}
            data-vy="product-inspect-dialog-amount"
          />
        </Box>

        {isPointBased ? (
          <PointInTimeEditor
            points={points}
            sessionTotal={sessionTotal}
            disabled={inFlight}
            onPointsChange={setPoints}
            mode={isPointInTime ? 'single' : 'milestones'}
          />
        ) : (
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <VyDatePicker
              label="Start Date"
              value={startDate}
              onChange={setStartDate}
              disabled={inFlight || !hasContract}
              sx={{ flex: 1 }}
            />
            <VyDatePicker
              label="End Date"
              value={endDate}
              onChange={setEndDate}
              disabled={inFlight || !hasContract}
              sx={{ flex: 1 }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={applyDisabled}
          data-vy="product-inspect-dialog-apply"
        >
          {inFlight ? 'Applying…' : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
